import { useCloseMultipleFlags, useSearchFlags } from '@actions';
import { ReactComponent as BlockIcon } from '@assets/icons/V4/block-icon.svg';
import { LoadingSpinner } from '@components/LoadingSpinner/LoadingSpinner';
import TableHeaderInfoCard from '@components/V3/TableHeaderInfoCard';
import Button from '@components/V4/Button';
import Table, { SortOrder } from '@components/V4/Table/Table';
import { useAuthState } from '@contexts/AuthContext';
import { useQueryParamsState } from '@hooks';
import { flagApis } from '@shared/apis';
import {
  Enrollment,
  Flag,
  FlagTypes,
  MeetingWithSessionId,
  ModelFlagTypes,
  UserRole,
} from '@shared/common';
import apiClient from 'apps/agora/src/contexts/AuthContext/apiClient';
import useToast from 'apps/agora/src/hooks/useToast';
import { getEndDateValue, getStartDateValue } from 'apps/agora/src/utils/helpers';
import { COLORS } from 'apps/agora/src/v3/global';
import EmptyStateCard from 'apps/agora/src/views/UserView/components/EmptyStateCard';
import moment, { Moment } from 'moment';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import DetailsModal from '../components/DetailsModal/DetailsModal';
import useDetailsModal, { ActiveModalView } from '../hooks/useDetailsModal';
import FlagsOverview from './components/FlagsOverview/FlagsOverview';
import FlagsTableHeader from './components/FlagsTableHeader';
import FlagsTableRow from './components/FlagsTableRow';
import { UsersHealth } from './components/UsersHealth/UsersHealth';

const useQuery = () => new URLSearchParams(useLocation().search);

const Flags = () => {
  const query = useQuery();
  const toast = useToast();
  const currentUser = useAuthState();

  const sortBy = query.get('sortBy') || null;
  const sortOrder = (query.get('sortOrder') || null) as SortOrder;
  const tab = query.get('tab') || 'Flags';
  const initialIsActive = query.get('isActive') || '0';

  const defaultStartDate = moment().subtract(3, 'months').startOf('month').toISOString();
  const defaultEndDate = moment().toISOString();

  const [searchParams, setSearchParams] = useQueryParamsState<any>({
    page: '1',
    limit: '10',
    sortBy: sortBy,
    sortOrder: sortOrder,
    tab: tab,
    isActive: initialIsActive,
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState('');
  const [isActive, setIsActive] = useState(initialIsActive);
  const [flagType, setFlagType] = useState<string | undefined>(undefined);
  const [startRange, setStartRange] = useState<string | undefined>();
  const [endRange, setEndRange] = useState<string | undefined>();
  const [courseId, setCourseId] = useState();
  const [studentId, setStudentId] = useState();
  const [mentorId, setMentorId] = useState();
  const [selectedFlags, setSelectedFlags] = useState<string[]>([]);
  const [userStatus, setUserStatus] = useState('indeterminate');
  const [isExportingFlags, setIsExportingFlags] = useState(false);

  const currentFlagFilters = {
    sort: (searchParams.sortOrder || 'desc') as 'asc' | 'desc',
    sortField: (searchParams.sortBy || 'date') as 'date' | 'student' | 'mentor',
    search,
    isActive,
    flagType,
    startRange,
    endRange,
    courseId,
    studentId,
    mentorId,
    userStatus,
  };

  const {
    data: flags,
    isLoading: isLoadingSearchFlags,
    refetch: refetchSearchFlags,
  } = useSearchFlags({
    ...currentFlagFilters,
    page: currentPage,
    limit: pageSize,
  });

  const { mutate: markAsClosed } = useCloseMultipleFlags({
    onSuccess: () => {
      toast.success('Selected flags are now marked as closed');
      setSelectedFlags([]);
      refetchSearchFlags();
    },
    onError: () => {
      toast.error("Couldn't mark selected flags as closed");
    },
  });

  const flagTypeChangeHandler = (flagType: string | undefined) => {
    setFlagType(flagType);
    setCurrentPage(1);
    setSearchParams({
      flagType: flagType || null,
      page: 1,
    });
  };

  const courseChangeHandler = (courseId: any) => {
    setCourseId(courseId);
    setCurrentPage(1);
    setSearchParams({
      courseId: courseId || null,
      page: 1,
    });
  };

  const studentChangeHandler = (studentId: any) => {
    setStudentId(studentId);
    setCurrentPage(1);
    setSearchParams({
      studentId: studentId || null,
      page: 1,
    });
  };

  const mentorChangeHandler = (mentorId: any) => {
    setMentorId(mentorId);
    setCurrentPage(1);
    setSearchParams({
      mentorId: mentorId || null,
      page: 1,
    });
  };

  const searchChangeHandler = (search: string) => {
    setSearch(search);
    setCurrentPage(1);
    setSearchParams({
      search: search || null,
      page: 1,
    });
  };

  const activeFlagsChangeHandler = (active: string) => {
    setIsActive(active);
    setCurrentPage(1);
    setSearchParams({
      isActive: active,
      page: 1,
    });
  };

  const addFlagHandler = (flag: string) => {
    setSelectedFlags((prevSelectedFlags) => [...prevSelectedFlags, flag]);
  };

  const removeFlagHandler = (flag: string) => {
    setSelectedFlags((prevSelectedFlags) => prevSelectedFlags.filter((item) => item !== flag));
  };

  const selectedTabChangeHandler = (tab: string) => {
    setSearchParams({ tab });
  };

  const userStatusChangeHandler = (userStatus: string) => {
    setUserStatus(userStatus);
    setCurrentPage(1);
    setSearchParams({
      userStatus: userStatus,
      page: 1,
    });
  };

  const getRelevantModal = (
    flagType: FlagTypes,
    modelFlagTypes: ModelFlagTypes
  ): ActiveModalView => {
    switch (flagType) {
      case FlagTypes.FIRST_MEETING_NOT_SCHEDULED_IN_3_DAYS:
      case FlagTypes.FIRST_MEETING_NOT_SCHEDULED_IN_5_DAYS:
      case FlagTypes.FIRST_MEETING_NOT_HELD_IN_7_DAYS:
      case FlagTypes.FIRST_MEETING_NOT_HELD_IN_10_DAYS:
      case FlagTypes.NEXT_MEETING_FURTHER_THAN_62_DAYS:
      case FlagTypes.MAX_DURATION_COURSE_EXCEEDED:
        return { page: 'enrollment' };

      case FlagTypes.BILLABLE_TIME_LESS_THAN_50_MINUTES:
      case FlagTypes.NO_MEETING_SCHEDULED_AFTER_48_HOURS_SIENCE_LAST_MEETING_HELD:
        return { page: 'session' };

      case FlagTypes.NO_FEEDBACK_AFTER_48_HOURS_SINCE_MEETING_HELD:
        return { page: 'session', tab: 'feedback' };

      case FlagTypes.NO_UNI_CHOICES_COMPLETED:
        return { page: 'student', tab: 'universities' };

      case FlagTypes.LOW_NPS:
      case FlagTypes.DISENGAGED_USER:
        return {
          page:
            modelFlagTypes === ModelFlagTypes.STUDENT || modelFlagTypes === ModelFlagTypes.PARENT
              ? 'student'
              : 'mentor',
        };

      case FlagTypes.PAST_MEETING_WITH_INVALID_STATUS:
        return { page: 'session', tab: 'meetings' };

      case FlagTypes.LOW_PARENT_SESSION_FEEDBACK:
        return { page: 'session', tab: 'feedback' };

      case FlagTypes.MENTOR_CHANGED:
        return { page: 'enrollment' };
      case FlagTypes.NO_ADMISSION_RESULTS_COMPLETED:
        return { page: 'student', tab: 'universities' };
      default:
        return { page: 'enrollment' };
    }
  };

  const {
    activeModalView,
    isModalOpen,
    studentId: detailsModalStudentId,
    mentorId: detailsModalMentorId,
    sessionId,
    enrollmentId,
    openModal,
    closeModal,
  } = useDetailsModal({ page: 'student' });

  const showMoreHandler = (modelType: ModelFlagTypes, flagType: FlagTypes, ref: Flag['ref']) => {
    const activeModalView = getRelevantModal(flagType, modelType);

    const studentId =
      modelType === ModelFlagTypes.STUDENT || modelType === ModelFlagTypes.PARENT
        ? (ref as MeetingWithSessionId)._id
        : undefined;

    const mentorId =
      modelType === ModelFlagTypes.MENTOR ? (ref as MeetingWithSessionId)._id : undefined;

    openModal(activeModalView, {
      enrollmentId: modelType === ModelFlagTypes.ENROLLMENT ? (ref as Enrollment)._id : undefined,
      sessionId:
        modelType === ModelFlagTypes.MEETING || modelType === ModelFlagTypes.SESSION
          ? (ref as MeetingWithSessionId).sessionId
          : undefined,
      studentId: studentId,
      mentorId: mentorId,
    });
  };

  const getRangePickerValue = (): [Moment, Moment] | null => {
    if (!startRange || !endRange) return null;
    return [moment(startRange), moment(endRange)];
  };

  const rangePickerChangeHandler = (dates: [Moment | null, Moment | null]) => {
    const start = dates?.[0] ? getStartDateValue(dates?.[0]) : null;
    const end = dates?.[1] ? getEndDateValue(dates?.[1]) : null;

    setStartRange(start ? start.toISOString() : undefined);
    setEndRange(end ? end.toISOString() : undefined);
    setSearchParams({
      startRange: start ? start.toISOString() : null,
      endRange: end ? end.toISOString() : null,
      page: 1,
    });
  };

  const sortChangeHandler = (field: string, order: SortOrder) => {
    setSearchParams({
      sortBy: order ? field : null,
      sortOrder: order || null,
      page: 1,
    });
  };

  const markSelectedAsClosedHandler = () => {
    markAsClosed({
      flagIds: selectedFlags,
      closedBy: currentUser?.userId,
    });
  };

  const mentorModalOpenHandler = (mentorId: string) => {
    openModal(
      { page: 'mentor' },
      {
        mentorId: mentorId,
      }
    );
  };

  const openStudentDetailsModalHandler = (studentId: string) => {
    openModal(
      { page: 'student' },
      {
        studentId: studentId,
      }
    );
  };

  const exportCurrentFlagsHandler = async () => {
    try {
      setIsExportingFlags(true);

      const response = await apiClient.get(flagApis.exportFlagsCSV.endpoint, {
        params: currentFlagFilters,
        responseType: 'blob',
      });

      const contentDisposition = response.headers['content-disposition'];
      const fileName =
        contentDisposition?.match(/filename="?([^"]+)"?/)?.[1] ||
        `flags-export-${moment().format('YYYY-MM-DD')}.csv`;

      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to export current flags');
    } finally {
      setIsExportingFlags(false);
    }
  };

  const renderTabContent = () => {
    switch (searchParams.tab) {
      case 'Flags':
        return (
          <Table
            className="p-6 py-8"
            pagination={{
              pageSize: pageSize !== null ? pageSize : 10,
              total: flags?.total,
              currentPage: currentPage !== null ? currentPage : 1,
              onCurrentPageChange: (page) => setCurrentPage(page),
              onPageSizeChange: (size) => setPageSize(size),
            }}
          >
            <Table.Header>
              <Table.Row isHeader>
                <Table.HeaderCell>Flag</Table.HeaderCell>
                <Table.HeaderCell
                  onSort={(order) => sortChangeHandler('student', order)}
                  sortOrder={sortBy === 'student' ? sortOrder : undefined}
                >
                  Student
                </Table.HeaderCell>
                <Table.HeaderCell
                  onSort={(order) => sortChangeHandler('mentor', order)}
                  sortOrder={sortBy === 'mentor' ? sortOrder : undefined}
                >
                  Mentor
                </Table.HeaderCell>
                <Table.HeaderCell>Course</Table.HeaderCell>
                <Table.HeaderCell
                  onSort={(order) => sortChangeHandler('date', order)}
                  sortOrder={sortBy === 'date' ? sortOrder : undefined}
                >
                  Date
                </Table.HeaderCell>
                <Table.HeaderCell>Comment</Table.HeaderCell>
                <Table.HeaderCell>Actions</Table.HeaderCell>
              </Table.Row>
            </Table.Header>

            <Table.Body>
              {!isLoadingSearchFlags ? (
                flags?.flags?.map((item) => (
                  <FlagsTableRow
                    key={item?._id}
                    flagData={item}
                    refetchSearchFlags={refetchSearchFlags}
                    currentUserId={currentUser?.userId}
                    addFlagHandler={addFlagHandler}
                    removeFlagHandler={removeFlagHandler}
                    showMoreHandler={showMoreHandler}
                    openStudentProfileHandler={openStudentDetailsModalHandler}
                    openMentorProfileHandler={mentorModalOpenHandler}
                  />
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="h-64">
                    <div className="w-full h-full flex justify-center items-center">
                      <LoadingSpinner className="w-5 h-5" />
                    </div>
                  </td>
                </tr>
              )}

              {!isLoadingSearchFlags && !flags?.flags?.length && (
                <tr>
                  <td colSpan={6} className="h-64">
                    <EmptyStateCard
                      icon={<BlockIcon className="text-customGrey" />}
                      text={'No results.'}
                    />
                  </td>
                </tr>
              )}
            </Table.Body>
          </Table>
        );

      case 'Overview':
        return (
          <div className="table-container min-h-full">
            <FlagsOverview
              search={search}
              isActive={isActive}
              flagType={flagType}
              startRange={startRange ?? defaultStartDate}
              endRange={endRange ?? defaultEndDate}
              studentId={studentId}
              mentorId={mentorId}
              courseId={courseId}
            />
          </div>
        );

      case 'MentorsHealth':
        return <UsersHealth userType={UserRole.Mentor} />;

      case 'StudentsHealth':
        return <UsersHealth userType={UserRole.Student} />;

      default:
        return null;
    }
  };
  const statsCards = [
    { label: 'Total flags', value: flags?.stats?.totalFlags ?? 0 },
    { label: 'Closed flags', value: flags?.stats?.closedFlags ?? 0 },
    { label: 'Pending review', value: flags?.stats?.pendingReviewFlags ?? 0 },
  ];

  return (
    <div className="bg-surfaceBackground text-white min-h-[calc(100vh-74px)]">
      <FlagsTableHeader
        flagTypeHandler={flagTypeChangeHandler}
        rangePickerChangeHandler={rangePickerChangeHandler}
        courseIdHandler={courseChangeHandler}
        studentIdHandler={studentChangeHandler}
        mentorIdHandler={mentorChangeHandler}
        searchHandler={searchChangeHandler}
        activeFlagsHandler={activeFlagsChangeHandler}
        selectedTabHandler={selectedTabChangeHandler}
        userStatusHandler={userStatusChangeHandler}
        selectedTab={searchParams.tab}
        flagType={flagType}
        courseId={courseId}
        studentId={studentId}
        mentorId={mentorId}
        isActive={isActive}
        getRangePickerValue={getRangePickerValue}
        search={search}
      />

      {searchParams.tab === 'Flags' && (
        <div
          className="px-8 py-6"
          style={{
            backgroundColor: COLORS.DARK_GREY_ALT,
          }}
        >
          {isLoadingSearchFlags ? (
            <div className="flex justify-center items-center min-h-[100px]">
              <LoadingSpinner size="normal" />
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <div className="grid gap-4 grid-cols-1 sm:grid-cols-3">
                {statsCards.map((card) => (
                  <TableHeaderInfoCard
                    key={card.label}
                    title={card.value}
                    description={card.label}
                    fullWidth
                  />
                ))}
              </div>
              <div className="flex justify-end">
                <Button
                  buttonText="Export current flags"
                  variant="secondary"
                  onClick={exportCurrentFlagsHandler}
                  isLoading={isExportingFlags}
                  disabled={isLoadingSearchFlags || isExportingFlags || !flags?.total}
                />
              </div>
            </div>
          )}
        </div>
      )}

      <section className="relative">
        {!!selectedFlags.length && (
          <Button
            className="absolute top-4 right-6"
            buttonText={'Mark selected as completed'}
            variant="secondary-red"
            onClick={markSelectedAsClosedHandler}
          />
        )}
        {renderTabContent()}
      </section>

      {isModalOpen && (
        <DetailsModal
          {...activeModalView}
          studentId={detailsModalStudentId}
          mentorId={detailsModalMentorId}
          enrollmentId={enrollmentId}
          sessionId={sessionId}
          isOpen={true}
          onClose={closeModal}
        />
      )}
    </div>
  );
};

export default Flags;
